import React, { useEffect, useRef, useState } from 'react';
import { Dispatch, RootState } from '../../store/store';
import { connect } from 'react-redux';
import SpinnerLoader from '../SpinnerLoader/SpinnerLoader';
import CycleSettings from '../../enums/cycle-setting.enum';
import { actions } from './constants';
import { PreHeatSettingsEditable } from '../../types/cycle-settings-types';
import { CycleSettingAction } from '../../enums/action';
import { SingleFormWithUpdate } from '../PostWeldSettings/components/SingleFormWithUpdate';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import { ElementSize } from '../../types/element-size';
import { useTranslation } from 'react-i18next';
import { ChoosePreHeatRecipe } from './components/ChoosePreHeatRecipeModal';
import { convertRecipeToPreHeatSettings } from '../PostWeldSettings/utils';
import { IOnUpdateSettingField } from '../PostWeldSettings/PostWeldSettings';
import CycleMeasurement from '../../enums/cycle-measurement';
import CycleMeasurementSymbol from '../../enums/cycle-measurement-symbol';
import getMeasurementPreference from '../../utils/getMeasurementPreference';
import { convertAbsoluteTemperature, convertTemperatureDelta } from '../../utils/temperature-unit-conversion';
import { createPreHeatRecipeFieldValidationSchema } from '../../utils/recipe-unit-validation';
import { IDbCycleSettings } from '../../store/models/cycles';
import UnitMismatchWarning, {
  getControllerMeasurement,
  hasMixedContractChannelUnits,
  hasMixedContractControllerUnits as getHasMixedContractControllerUnits,
} from '../CycleSettings/components/UnitMismatchWarning';

type ComponentProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch> & { cycleId: string };

const PreHeatSettings: React.FC<ComponentProps> = ({
  cycleId,
  getSettings,
  clearSettings,
  updateSettings,
  getDbPreHeatRecipe,
  cycleDbSettings,
  updateDbSettings,
  measurement,
  controllerMeasurement,
  hasMixedContractControllerUnits,
  // createPreHeatRecipeFromScratch,
}) => {
  const [initialSettings, setInitialSettings] = useState<PreHeatSettingsEditable>(null);
  const lastCycleDbSettingsRef = useRef<IDbCycleSettings | null>(null);
  const appliedDbSettingsVersion = useRef<string | null>(null);

  if (cycleDbSettings) {
    lastCycleDbSettingsRef.current = cycleDbSettings;
  }

  const effectiveCycleDbSettings = cycleDbSettings ?? lastCycleDbSettingsRef.current;

  const { t } = useTranslation();

  const convertPreHeatSettingsForDisplay = (settings: PreHeatSettingsEditable): PreHeatSettingsEditable => {
    if (!settings || measurement === CycleMeasurement.Celsius) {
      return settings;
    }

    return {
      ...settings,
      rate: convertTemperatureDelta(settings.rate, CycleMeasurement.Celsius, measurement) as number,
      finalTemp: convertAbsoluteTemperature(settings.finalTemp, CycleMeasurement.Celsius, measurement) as number,
      soakUpper: convertTemperatureDelta(settings.soakUpper, CycleMeasurement.Celsius, measurement) as number,
      soakLower: convertTemperatureDelta(settings.soakLower, CycleMeasurement.Celsius, measurement) as number,
    };
  };

  const convertPreHeatSettingsForApi = (settings: PreHeatSettingsEditable): PreHeatSettingsEditable => {
    if (!settings || measurement === CycleMeasurement.Celsius) {
      return settings;
    }

    return {
      ...settings,
      rate: convertTemperatureDelta(settings.rate, measurement, CycleMeasurement.Celsius) as number,
      finalTemp: convertAbsoluteTemperature(settings.finalTemp, measurement, CycleMeasurement.Celsius) as number,
      soakUpper: convertTemperatureDelta(settings.soakUpper, measurement, CycleMeasurement.Celsius) as number,
      soakLower: convertTemperatureDelta(settings.soakLower, measurement, CycleMeasurement.Celsius) as number,
    };
  };

  const convertPreHeatFieldValueForApi = (key: string, value?: string) => {
    if (measurement === CycleMeasurement.Celsius) {
      return value;
    }

    if (key === 'finalTemp') {
      return String(convertAbsoluteTemperature(Number(value), measurement, CycleMeasurement.Celsius));
    }

    if (key === 'rate' || key === 'soakUpper' || key === 'soakLower') {
      return String(convertTemperatureDelta(Number(value), measurement, CycleMeasurement.Celsius));
    }

    return value;
  };

  const getFieldLabel = (item: (typeof actions)[number]) => {
    const symbol = CycleMeasurementSymbol[measurement];
    const suffix = item.editableKey === 'rate' ? `${symbol}/h` : symbol;

    return `${item.labelText} (${suffix})`;
  };

  const onUpdateSettingsHandler = async (action: CycleSettingAction, value?: string) => {
    const payload = { action };
    if (value) {
      payload['value'] = value;
    }
    await updateSettings({
      id: cycleId,
      payload: payload,
    });
  };

  //used only on first load
  const isUpdatedSteps = useRef(false);
  const fetchData = async () => {
    await getSettings({ id: cycleId, query: { name: CycleSettings.PreHeat } });

    if (!effectiveCycleDbSettings) {
      return;
    }

    const dbSettingsVersion = `${effectiveCycleDbSettings.id}-${effectiveCycleDbSettings.updated_at}-${measurement}`;
    if (!isUpdatedSteps.current || appliedDbSettingsVersion.current !== dbSettingsVersion) {
      setInitialSettings(convertPreHeatSettingsForDisplay(convertRecipeToPreHeatSettings(effectiveCycleDbSettings)));
      isUpdatedSteps.current = true;
      appliedDbSettingsVersion.current = dbSettingsVersion;
    }
  };

  const onUpdateAllHandler = async () => {
    const apiInitialSettings = convertPreHeatSettingsForApi(initialSettings);

    await updateDbSettings({
      id: cycleId,
      payload: { cycleDbSettings: apiInitialSettings },
    });

    // As-entered (not Celsius-converted) values for the controller - the
    // backend already knows the contract's measurement and only converts if
    // the controller's unit actually differs. Sending a pre-converted value
    // here would force an unnecessary, lossy round-trip through Celsius even
    // when the contract and controller already share a unit.
    const wholeRecipe = {
      holdingTemp: initialSettings.finalTemp,
      lowerHoldTolerance: initialSettings.soakLower,
      upperHoldTolerance: initialSettings.soakUpper,
      rate: initialSettings.rate,
    };
    const stringData = JSON.stringify(wholeRecipe);
    await onUpdateSettingsHandler(CycleSettingAction.SET_CYCLE_UPDATE_WHOLE_PRE_HEAT, stringData);
  };

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, key: keyof PreHeatSettingsEditable) => {
    const type = event.target.type as React.InputHTMLAttributes<HTMLInputElement>['type'];
    const inputValue = event.target.value;
    const value = type === 'number' ? +inputValue : inputValue;

    setInitialSettings({ ...initialSettings, [key]: value });
  };

  const onUpdateSettingField = async ({ action, key, value }: IOnUpdateSettingField) => {
    if (key) {
      const data = {};
      data[key] = convertPreHeatFieldValueForApi(key, value);

      await updateDbSettings({
        id: cycleId,
        payload: {
          cycleDbSettings: data,
        },
      });
    }

    // Send the raw, as-entered value (not the Celsius-converted one used for
    // storage) to the controller - see the comment in onUpdateAllHandler.
    await onUpdateSettingsHandler(action, value);
  };

  useEffect(() => {
    fetchData();
  }, [cycleDbSettings, measurement]);

  useEffect(() => {
    return () => {
      clearSettings();
      appliedDbSettingsVersion.current = null;
    };
  }, []);

  if (!effectiveCycleDbSettings || !initialSettings) {
    return (
      <div className="c-pre-heat-settings h-full w-full">
        <div className=" flex h-full w-full items-center justify-center">
          <SpinnerLoader />
        </div>
      </div>
    );
  }

  const onSelectDbRecipe = async (recipeId: string) => {
    const recipe = await getDbPreHeatRecipe(recipeId);
    if (recipe.mode === CycleSettings.PreHeat) {
      const data = convertPreHeatSettingsForDisplay({
        rate: recipe.rate,
        finalTemp: recipe.finalTemp,
        soakUpper: recipe.soakUpper,
        soakLower: recipe.soakLower,
      });
      setInitialSettings(data);
    }
  };

  // const onSavePreHeatRecipeIntoDb = async () => {
  //   const data: IPreHeatRecipeEditable = {
  //     ...initialSettings,
  //     name: '',
  //     mode: CycleSettings.PreHeat,
  //   }
  //   await createPreHeatRecipeFromScratch(data);
  // };

  return (
    <div className="c-pre-heat-settings h-full w-full">
      <div className="flex w-full justify-end gap-5">
        <ChoosePreHeatRecipe onSelect={onSelectDbRecipe} />
        {/* <InteractiveButton onClick={onSavePreHeatRecipeIntoDb}>{t('buttons.saveAsNewRecipe')}</InteractiveButton> */}
      </div>
      {hasMixedContractControllerUnits && <UnitMismatchWarning />}

      {actions.map((item) => {
        return (
          <SingleFormWithUpdate
            key={item.actionName}
            type={item.inputType}
            values={{
              [item.actionField]: initialSettings[item.editableKey],
            }}
            label={getFieldLabel(item)}
            currentValues={effectiveCycleDbSettings[item.actionField]}
            onChange={(e) => onChangeHandler(e, item.editableKey)}
            onSubmit={(val) =>
              onUpdateSettingField({
                action: item.actionName,
                key: item.editableKey,
                value: String(val[item.actionField]),
              })
            }
            validationSchema={createPreHeatRecipeFieldValidationSchema(measurement, item.actionField, item.editableKey, controllerMeasurement)}
          />
        );
      })}

      <div className="flex w-full justify-center py-10">
        <InteractiveButton onClick={onUpdateAllHandler} size={ElementSize.XL}>
          {t('buttons.updateAll')}
        </InteractiveButton>
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  cycleDbSettings: state.cycles.cycleDbSettings,
  measurement: getMeasurementPreference(state.contracts.contract, state.locations.currentLocation),
  controllerMeasurement: getControllerMeasurement(state.cycles.cycleIoTChannelsSettings),
  hasMixedContractControllerUnits:
    getHasMixedContractControllerUnits(
      state.cycles.cycle,
      getMeasurementPreference(state.contracts.contract, state.locations.currentLocation),
    ) ||
    hasMixedContractChannelUnits(
      state.cycles.cycleIoTChannelsSettings,
      getMeasurementPreference(state.contracts.contract, state.locations.currentLocation),
    ),
});

const mapDispatch = (dispatch: Dispatch) => ({
  getSettings: dispatch.cycles.getSettingsByMode,
  clearSettings: dispatch.cycles.clearSettings,
  updateSettings: dispatch.cycles.updateSettings,
  updateDbSettings: dispatch.cycles.updateDbSettings,
  getDbPreHeatRecipe: dispatch.recipes.getRecipe,
  createPreHeatRecipeFromScratch: dispatch.recipes.createPreHeatRecipeFromScratch,
});

export default connect(mapState, mapDispatch)(PreHeatSettings);
